import {observer} from "mobx-react";
import {useState} from "react";
import className from "classnames";
import styles from "./styles/AccordionTerrace.module.scss";
import editStyles from "../../SchoolBlocks/styles/EditPane.module.scss";
import {RequiredInput} from "../../SchoolBlocks/EditBlockComponents/EditBlockInputs/RequiredInput";
import MarkdownEditor from "../../../editors/MarkdownEditor";
import WatsonApi from "../../../../backends/WatsonApi";
import {runInAction} from "mobx";
import NotificationManager from "../../../notifications/NotificationManager";
import {useStores} from "../../../../stores/useStores";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import SchoolBlocksLoadingIndicator from "../../../utilities/SchoolBlocksLoadingIndicator";
import FontAwesome from "../../../utilities/FontAwesome";
import {ContactSupport} from "../../../misc/ContactSupport";
import NewTag from "../../../admin/sidebar/items/NewTag";

const AccordionItemEditor = observer((props: {
    blockObj: IAccordionTerrace,
    index: number,
    item?: IAccordionItem,
    parentItem?: IAccordionItem,
    handleSave?: () => void,
    handleCancel: () => void,
    accordionList: IAccordionItem[]
}) => {
    const {organizationStore} = useStores();

    const [title, setTitle] = useState(props?.item?.title || "");
    const [summary, setSummary] = useState(props?.item?.description || "");
    const [content, setContent] = useState(props?.item?.content || "");
    const [addContent, setAddContent] = useState(props?.item?.has_content);

    const handleSave = async () => {
        try {
            const client = await WatsonApi();

            let response;
            if (props.item) {
                response = await client.apis.organizations.organizations_terraces_accordion_items_partial_update({
                    organization_pk: props.blockObj.organization_id,
                    terrace_pk: props.blockObj.id,
                    id: (props.item as IAccordionItem).id,
                    data: {
                        title,
                        description: summary,
                        content: content,
                    }
                });
            } else {
                let path = '';

                if (props.parentItem && props.blockObj.items) {
                    const parentPath = props.parentItem.path;
                    const siblings = props.blockObj.items.filter(item => {
                        return item.path && item.path.startsWith(parentPath + '.')
                            && item.path.split('.').length === parentPath.split('.').length + 1;
                    });
                    let lastSiblingNum = 0;
                    if (siblings.length > 0) {
                        const lastSiblingPathParts = siblings[siblings.length-1].path.split(".");
                        lastSiblingNum = parseInt(lastSiblingPathParts[lastSiblingPathParts.length-1]);
                    }

                    const newChildIndex = lastSiblingNum + 1;
                    const paddedIndex = String(newChildIndex).padStart(3, '0');
                    path = `${parentPath}.${paddedIndex}`;
                }
                else {
                    let lastItemNum = 0;
                    if ((props.blockObj.items as IAccordionItem[]).length > 0) {
                        const lastItemPath = (props.blockObj.items as IAccordionItem[])[(props.blockObj.items as IAccordionItem[]).length - 1].path.split(".")[0];
                        lastItemNum = parseInt(lastItemPath.slice(lastItemPath.length - 3, lastItemPath.length));
                    }
                    path = `${(lastItemNum + 1).toString().padStart(3, "0")}`;
                }

                response = await client.apis.organizations.organizations_terraces_accordion_items_create({
                    organization_pk: props.blockObj.organization_id,
                    terrace_pk: props.blockObj.id,
                    data: {
                        title,
                        description: summary,
                        content: content,
                        path,
                    },
                });
            }

            runInAction(() => {
                const updatedItem = JSON.parse(response.data);
                if (props.item) {
                    props.blockObj.items = (props.blockObj.items as IAccordionItem[]).map(item => {
                        return item.id === (props.item as IAccordionItem).id ? updatedItem : item;
                    })
                } else {
                    (props.blockObj.items as IAccordionItem[]).push(updatedItem);
                    props.blockObj.items.sort((a, b) => a.path.localeCompare(b.path));
                }
            });
            NotificationManager.success("Terrace successfully saved!");
            props.handleCancel();
        } catch (e) {
            NotificationManager.error("Something went wrong! Please try again.");
        }
    }

    const titleIsValid = title && (title?.length > 0);


    const markdownEditorClassName = className({
        [styles.markdownEditor]: true,
        [styles.markdownEditorContent]: true,
    })

    const handleSubmit = async () => {
        await handleSave();
    }

    const handleDelete = async () => {
        try {
            const client = await WatsonApi();
            await client.apis.organizations.organizations_terraces_accordion_items_delete({
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
                id: (props.item as IAccordionItem).id,
            })
            runInAction(() => {
                props.blockObj.items = (props.blockObj.items as IAccordionItem[]).filter(item => {
                    return item.id !== (props.item as IAccordionItem).id;
                })
            })
            NotificationManager.success("Accordion Item deleted.");
        } catch (e) {
           NotificationManager.error(JSON.parse(e.response.data).message);
        }
    }

    const handleMessageChange = (value) => {
        setContent(value);
    };
    const handleSummaryChange = (value) => {
        setSummary(value);
    }
    const handleTitleChange = (event) => {
        setTitle(event.target.value);
    };

    const getContent = async () => {
        try {
            const client = await WatsonApi();
            const result = await client.apis.organizations.organizations_terraces_accordion_items_read({
                organization_pk: organizationStore.currentOrganization.id,
                terrace_pk: (props.item as IAccordionItem).terrace_id,
                id: (props.item as IAccordionItem).id,
                expand: ["content"],
            });
            return result.obj;
        } catch (e) {
            NotificationManager.error(e.response?.body?.detail);
        }
    }

    useAsyncEffect(async () => {
        if (props.item && props.item.has_content && !props.item?.content) {
            const itemContent = await getContent();
            if (itemContent) setContent(itemContent.content);
        }
    },[])

    return <>
        <div className={editStyles.inputGroup}>
            <label>Title</label>
            <div>
                <RequiredInput
                    title="Message Title"
                    className="form-control"
                    type="text"
                    onChange={handleTitleChange}
                    value={title}
                    valid={!!titleIsValid}
                />
            </div>
        </div>

        <div className={editStyles.inputGroup}>
            <label>Summary (Optional)</label>
            <MarkdownEditor
                handleTextChange={handleSummaryChange}
                value={summary || ''}
                editorClassName={styles.markdownEditor}
                className={styles.editor}
            />
        </div>
        {addContent ? <div className={editStyles.inputGroup}>
            <label>Page Content <span
                className={styles.tooltip}
                data-content={"Content will only be shown\nin the Accordion Item Modal\n" +
                    "and on the Accordion Item Page.\n" +
                    "Navigate to these locations respectively\n by clicking 'View More'\n" +
                    "on an Accordion Item with content, \n" +
                    "and by copying the page link from\n the 'Share' button in the modal."}
                style={{marginLeft: ".25rem"}}
            >
            <FontAwesome prefix={'fas'} name={'fa-question-circle'}/>
        </span> (Optional)</label>
            {(props.item?.has_content && !content) ? <div><SchoolBlocksLoadingIndicator/></div> : <MarkdownEditor
                handleTextChange={handleMessageChange}
                value={content || ''}
                editorClassName={markdownEditorClassName}
                className={styles.editor}
            />}
        </div> : <div className={styles.editorButtons}>
            {process.env.DOCMERSION_ENABLED && <div>
                {!organizationStore.organization.billing_products.docmersion &&
                    <ContactSupport
                        message={"Adding content requires a Docmersion Subscription."}
                        className={styles.docmersionText}
                    />}
                <div>
                    <button disabled={!organizationStore.organization.billing_products.docmersion}
                            className={`${styles.controlButton} ${styles.saveButton} btn`}
                            onClick={() => setAddContent(true)}>
                        Add Content <NewTag />
                    </button>
                </div>
            </div>}
        </div>
        }
        <div className={styles.editorButtons}>
            <button type="button"
                    className={`${styles.controlButton} ${styles.saveButton} btn ${!titleIsValid ? styles.disabled : ''}`}
                    disabled={!titleIsValid}
                    onClick={handleSubmit}
            >
                Save Accordion Item
            </button>
            <button type="button"
                    className={`${styles.controlButton} ${styles.closeButton} btn`}
                    disabled={false}
                    onClick={props.handleCancel}
            >
                Cancel
            </button>
            {props.accordionList && props.item && <button type="button"
                                            className={`${styles.controlButton} ${styles.deleteButton} btn`}
                                            disabled={false}
                                            onClick={handleDelete}
            >
                Delete
            </button>}
        </div>
    </>
});

export default AccordionItemEditor;
